/**
 * @author      russell@zumper.com (Russell Middleton)
 * @copyright   Copyright (c) 2017, Zumper
 * @description Blueshift service wrapper, which serves as a singleton to access
 *              `window.blueshift`.
 */

import { USER_ID_LOCAL_STORAGE_KEY } from '@zcode/integrations'
import { localStorageSingleton } from '@zcode/nr-utils'
import { logger } from '../../../logger'
import { ON_BROWSER } from '../../env'
import { displayAmenity, displayBuildingAmenity } from '../../utils/amenity'
import { isProUser } from '../../utils/isProUser'
import addFilterPrefix from '../../utils/marketing/helpers/addFilterPrefix'
import { boolArrayToIntArray } from '../../utils/utils'
import {
  ACCOUNT_CREATION,
  ALERT,
  FAVORITE,
  LEAD,
  LIST_SEARCH,
  NOTIFY_ME_UNIT,
  STR_CLICK,
  VIEW,
} from './constants/event'
import getLeadType from './helpers/getLeadType'

// eslint-disable-next-line import/no-default-export
export default class Marketing {
  mapFiltersForBlueshift(filters = {}, cityState = '', cityStateHood = '') {
    const bFilters = filters
    // alerts and search filters have different naming conventions for min/max lat
    const minLat = bFilters.min_lat || bFilters.minLat
    const maxLat = bFilters.max_lat || bFilters.maxLat
    const minLng = bFilters.min_lng || bFilters.minLng
    const maxLng = bFilters.max_lng || bFilters.maxLng
    const listingAmenities = bFilters.listingAmenities || []
    const buildingAmenities = bFilters.buildingAmenities || []
    let { longTerm, shortTerm } = bFilters
    if (!longTerm && !shortTerm) {
      longTerm = true
    }

    return {
      amenities: listingAmenities.map((amenity) => displayAmenity(amenity)),
      bedrooms: boolArrayToIntArray(bFilters.bedrooms),
      building_amenities: buildingAmenities.map((amenity) => displayBuildingAmenity(amenity)),
      cats: bFilters.cats,
      cheap: bFilters.cheap,
      city: bFilters.city,
      city_state: cityState,
      city_state_hood: cityStateHood,
      dogs: bFilters.dogs,
      lat: minLat && maxLat ? (minLat + maxLat) * 0.5 : bFilters.lat,
      lng: minLng && maxLng ? (minLng + maxLng) * 0.5 : bFilters.lng,
      long_term: longTerm,
      max_lat: maxLat,
      max_lng: maxLng,
      max_price: !filters.customMaxPrice && filters.maxPrice === 5000 ? null : bFilters.maxPrice,
      min_lat: minLat,
      min_lng: minLng,
      min_price: bFilters.minPrice,
      neighborhood: bFilters.neighborhood,
      property_categories: bFilters.propertyTypes,
      short_term: !longTerm,
      state: bFilters.state,
    }
  }

  track(eventName = '', eventObj = {}) {
    if (ON_BROWSER && typeof window !== 'undefined') {
      logger.debug('blueshift track', eventName, eventObj)

      const { phone_number: phone } = eventObj
      let cleanPhone = phone
      let eventObjCopy = { ...eventObj, host: window.location.hostname }
      cleanPhone = cleanPhone && cleanPhone.indexOf('ext.') === -1 ? cleanPhone : null
      if (cleanPhone) {
        // blueshift phone must contain only numbers
        cleanPhone = cleanPhone.replace(/[^0-9]/g, '')
        if (cleanPhone.length) {
          cleanPhone = cleanPhone.charAt(0) === '1' ? `+${cleanPhone}` : `+1${cleanPhone}`
        }

        eventObjCopy = { ...eventObj, phone_number: cleanPhone }
      }
      try {
        if (window.dataLayer) {
          window.dataLayer.push({
            event: 'blueshift_track',
            blueshiftEventName: eventName,
            blueshiftEventObj: eventObjCopy,
          })
          this.pushResetEvent()
        } else if (window.blueshift) {
          window.blueshift.track(eventName, eventObj)
        }
      } catch (e) {
        logger.error('blueshift track failed:', eventName, eventObj, e)
      }
    }
  }

  trackPageload() {
    if (ON_BROWSER && typeof window !== 'undefined') {
      logger.debug('blueshift pageload')
      try {
        if (window.dataLayer) {
          window.dataLayer.push({ event: 'blueshift_pageload' })
        } else if (window.blueshift) {
          window.blueshift.pageload()
        }
      } catch (e) {
        logger.error('blueshift pageload failed:', e)
      }
    }
  }

  trackIdentify({
    firstName,
    lastName,
    email,
    phone,
    moveIn,
    joinedAt,
    userId,
    isFacebook,
    selectDate,
    traktorUserId,
    filters = {},
    roles,
    leaseLength,
    forYouQuestionsTimestamp,
    forYouResultsTimestamp,
    ...extra
  }) {
    const isPro = typeof roles !== 'undefined' && isProUser({ roles })
    const leaseLengthString = leaseLength === 'Long-term' ? 'long_term' : leaseLength?.toLowerCase()
    if (ON_BROWSER && typeof window !== 'undefined' && !isPro) {
      // blueshift phones will only be used for sms campaigns so exclude phones with extensions
      let cleanPhone = phone
      cleanPhone = cleanPhone && cleanPhone.indexOf('ext.') === -1 ? cleanPhone : null
      if (cleanPhone) {
        // blueshift phone must contain only numbers
        cleanPhone = cleanPhone.replace(/[^0-9]/g, '')
        if (cleanPhone.length) {
          cleanPhone = cleanPhone.charAt(0) === '1' ? `+${cleanPhone}` : `+1${cleanPhone}`
        }
      }
      const identifyObj = {
        // dataLayer definition defined in GTM dashboard
        blueshiftEmail: email,
        blueshiftUserId: userId,
        blueshiftFirstName: firstName,
        blueshiftHost: window.location.hostname,
        blueshiftLastName: lastName,
        blueshiftMoveIn: moveIn && new Date(moveIn).toISOString(),
        blueshiftJoinedAt: joinedAt && new Date(joinedAt * 1000).toISOString(),
        blueshiftTraktorUserId:
          traktorUserId || localStorageSingleton.get(USER_ID_LOCAL_STORAGE_KEY),
        blueshiftPhoneNumber: cleanPhone,
        blueshiftIsFacebook: isFacebook,
        blueshiftFilters: filters,
        blueshiftSelectDate: selectDate,
        blueshiftForYouQuestionsOn: forYouQuestionsTimestamp,
        blueshiftForYouResultsOn: forYouResultsTimestamp,
        blueshiftLeaseLength: leaseLengthString,
        ...extra,
      }

      logger.debug('blueshift identify', identifyObj)
      try {
        // triggering `blueshift.identify`. This event is defined in `Triggers` section
        // in Google Tag Manager Workspace
        if (window.dataLayer) {
          window.dataLayer.push({
            ...identifyObj,
            event: 'blueshift_identify',
          })
          this.pushResetEvent()
        } else if (window.blueshift) {
          window.blueshift.identify(identifyObj)
        }
      } catch (e) {
        logger.error('blueshift identify failed:', identifyObj, e)
      }
    }
  }

  trackSearch(detailIds, filters, cityState, cityStateHood, email) {
    this.trackIdentify({
      email,
      filters: addFilterPrefix(this.mapFiltersForBlueshift(filters), 'filter_'),
    })
    this.track(LIST_SEARCH, {
      email,
      event: LIST_SEARCH,
      event_metadata: this.mapFiltersForBlueshift(filters, cityState, cityStateHood),
      product_ids: detailIds,
    })
  }

  /**
   *
   * @param {number | null | undefined} buildingId
   * @param {string | number | null | undefined} listingId
   * @param {string | null | undefined} email
   * @param {boolean | null | undefined} [isShortTerm]
   */
  trackViewDetail(buildingId, listingId, email, isShortTerm) {
    const shortTerm = isShortTerm ? { short_term: true } : {}
    this.track(VIEW, {
      email,
      event: VIEW,
      products: [{ sku: buildingId ? `b${buildingId}` : listingId }],
      ...shortTerm,
    })
  }

  trackViewShortTermViewDetail(buildingId, listingId, email, isShortTerm) {
    const shortTerm = isShortTerm ? { short_term: true } : {}
    this.track(STR_CLICK, {
      email,
      event: STR_CLICK,
      products: [{ sku: buildingId ? `b${buildingId}` : listingId }],
      ...shortTerm,
    })
  }

  trackFavorite(buildingId, listingId, email) {
    this.track(FAVORITE, {
      email,
      event: FAVORITE,
      sku: buildingId ? `b${buildingId}` : listingId,
    })
  }

  trackSendLeads({ email, isFacebook, isMulti, listingIds }) {
    if (listingIds && listingIds.length) {
      this.track(LEAD, {
        email,
        event: LEAD,
        isFacebook,
        leadType: getLeadType({ isMulti }),
        sku: listingIds,
      })
    }
  }

  trackNotifyMeAlert({ email, firstName, lastName, productId }) {
    this.trackIdentify({
      email,
      firstName,
      lastName,
    })

    const extra = {}
    if (productId) {
      extra.product_ids = [productId]
      extra.sku = productId
    }

    this.track(NOTIFY_ME_UNIT, {
      email,
      event: NOTIFY_ME_UNIT,
      ...extra,
    })
  }

  trackAlert(
    filters = {},
    { customerId, email, firstName, fromMessage, lastName, name, origin, productId, searchId }
  ) {
    let cityState = ''
    let cityStateHood = ''
    if (filters.url) {
      const urlParts = filters.url.split('/')
      if (urlParts.length === 1) {
        cityState = urlParts[0]
      } else if (urlParts.length === 2) {
        cityState = urlParts[0]
        cityStateHood = `${urlParts[0]}/${urlParts[1]}`
      }
    }

    this.trackIdentify({
      email,
      firstName,
      lastName,
      filters: addFilterPrefix(this.mapFiltersForBlueshift(filters), 'alert_'),
    })

    const extra = {
      alert_origin: origin,
      alert_id: searchId,
      customer_id: customerId,
      email_enabled: true,
      from_message: fromMessage,
      push_enabled: false,
      name,
    }
    if (productId) {
      extra.product_ids = [productId]
      extra.sku = productId
    }
    if (!filters?.longTerm && filters?.shortTerm) {
      extra.alert_short_term = true
    }
    const eventMetadata = {
      ...this.mapFiltersForBlueshift(filters, cityState, cityStateHood),
      ...extra,
    }
    Object.keys(eventMetadata).forEach(
      (key) => eventMetadata[key] === undefined && delete eventMetadata[key]
    )
    this.track(ALERT, {
      email,
      event: ALERT,
      ...eventMetadata,
    })
  }

  trackListablesSearch(listables, filters, locationUrl, email) {
    const blueshiftDetailIds = listables.map((item) => {
      return item.buildingId ? `b${item.buildingId}` : item.listingId.toString()
    })
    const cityState = locationUrl && locationUrl.split('/')[0]
    const cityStateHood = locationUrl !== cityState && locationUrl
    this.trackSearch(blueshiftDetailIds, filters, cityState, cityStateHood, email)
  }

  trackAccountCreation(email) {
    this.track(ACCOUNT_CREATION, { email })
  }

  pushResetEvent() {
    // Push a reset event to clear blueshift event object since GTM recursively merges objects
    // resulting in event attributes from previous events possibly included in current event. This
    // does not send anything to blueshift
    window.dataLayer.push({
      event: 'blueshift_reset',
      blueshiftEventName: undefined,
      blueshiftEventObj: undefined,
      blueshiftFilters: undefined,
    })
  }
}
